import React, { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  fetchCurrencies,
  selectCurrencies,
} from "../../features/auth/store/currencySlice";
import {
  fetchReportSummary,
  selectReportDataupBalance,
  selectReportSeagmBalance,
  selectReportSummaryError,
  selectReportSummaryLoading,
  selectReportTotalUsers,
  selectReportTotalUsersCoinBalance,
  selectReportTotalUsersPointBalance,
} from "../../features/auth/store/reportSlice";
import { convertCurrencyAmount, formatCurrency } from "../../utils/utils";

const formatNumber = (value: number) => value.toLocaleString();

const CardSpinner = () => (
  <div className="flex items-center py-1">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
  </div>
);

type ReportCardProps = {
  title: string;
  value: React.ReactNode;
};

const ReportCard = ({ title, value }: ReportCardProps) => (
  <div className="bg-pry-25 rounded-xl min-w-0">
    <div className="col-6 py-4 px-3">
      <div className="row-2 justify-between items-center">
        <span className="satoshi-sm-medium-gray-600 break-words">{title}</span>
      </div>
      <span className="satoshi-xl-bold-gray-900">{value}</span>
    </div>
  </div>
);

export const Report = () => {
  const dispatch = useAppDispatch();
  const currencies = useAppSelector(selectCurrencies) ?? [];
  const totalUsersCoinBalance = useAppSelector(
    selectReportTotalUsersCoinBalance,
  );
  const totalUsersPointBalance = useAppSelector(
    selectReportTotalUsersPointBalance,
  );
  const seagmBalance = useAppSelector(selectReportSeagmBalance);
  const dataupBalance = useAppSelector(selectReportDataupBalance);
  const totalUsers = useAppSelector(selectReportTotalUsers);
  const reportLoading = useAppSelector(selectReportSummaryLoading);
  const reportError = useAppSelector(selectReportSummaryError);

  useEffect(() => {
    dispatch(fetchReportSummary());
    if (currencies.length === 0) {
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [currencies.length, dispatch]);

  const ngnCurrency = currencies.find(
    (currency) =>
      currency.code === "NGN" ||
      currency.symbol === "₦" ||
      currency.name.toLowerCase().includes("naira"),
  );
  const coinCurrency = currencies.find(
    (currency) => currency.symbol === "Coins",
  );
  const pointCurrency = currencies.find(
    (currency) => currency.symbol === "Points",
  );

  const formatNairaEquivalent = (
    amount: number,
    fromCurrency?: { exchange_rate: number | string } | null,
  ) => {
    if (!fromCurrency || !ngnCurrency) {
      return "";
    }

    const convertedAmount = convertCurrencyAmount(amount, fromCurrency, ngnCurrency);
    return ` (₦${formatCurrency(convertedAmount)})`;
  };

  if (reportLoading) {
    return (
      <div className="flex min-h-[220px] items-center justify-center rounded-xl bg-white">
        <CardSpinner />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
      <ReportCard
        title="Total Users Coins Balance"
        value={
          reportError
            ? "Error"
            : `${formatNumber(totalUsersCoinBalance ?? 0)}${formatNairaEquivalent(
                totalUsersCoinBalance ?? 0,
                coinCurrency,
              )}`
        }
      />

      <ReportCard
        title="Total Users Points Balance"
        value={
          reportError
            ? "Error"
            : `${formatNumber(totalUsersPointBalance ?? 0)}${formatNairaEquivalent(
                totalUsersPointBalance ?? 0,
                pointCurrency,
              )}`
        }
      />

      <ReportCard
        title="Seagm Credit Balance"
        value={reportError ? "Error" : formatNumber(seagmBalance ?? 0)}
      />

      <ReportCard
        title="DataUp Balance"
        value={reportError ? "Error" : `₦${formatCurrency(dataupBalance ?? 0)}`}
      />

      <ReportCard
        title="Total Users Onboarded"
        value={reportError ? "Error" : formatNumber(totalUsers ?? 0)}
      />
    </div>
  );
};
